import {
	FileExtension,
	FileType,
	IndemnityAttachmentTypeLabelMap,
	IndemnityStatus,
	SuretyAccountId,
	SuretyIndemnityAgreementId,
} from "@inrev/common";
import { useUploadCompletedIndemnityAgreement } from "@portal/api/account/agent";
import { ConfirmationModalClickWrapper } from "@portal/components/layout/ConfirmationModalClickWrapper";
import { LoadingModal } from "@portal/components/layout/LoadingModal";
import { Button } from "@portal/components/primitives";
import { LocalFileUpload } from "@portal/components/ui/LocalFileUpload";
import { useState } from "react";
import { LocallyUploadedFile } from "../../../../../../../../types";
import { IndemnityAgreementAttachmentCard } from "./IndemnityAgreementAttachmentCard";

export const IndemnityAgreementFileUpload = ({
	suretyAccountId,
	indemnityAgreementId,
	indemnityAgreementStatus,
	additionalButtons,
}: {
	suretyAccountId: SuretyAccountId;
	indemnityAgreementId: SuretyIndemnityAgreementId;
	indemnityAgreementStatus: IndemnityStatus;
	additionalButtons?: React.ReactNode;
}) => {
	const [indemnityAgreementFileUpload, setIndemnityAgreementFileUpload] = useState<
		LocallyUploadedFile[]
	>([]);

	const { uploadCompletedAgreement, uploadCompletedAgreementIsLoading } =
		useUploadCompletedIndemnityAgreement(suretyAccountId, indemnityAgreementId);

	const handleUploadCompletedAgreement = () => {
		uploadCompletedAgreement({
			fileId: indemnityAgreementFileUpload[0].id,
			indemnityAgreementId: indemnityAgreementId,
		});
		setIndemnityAgreementFileUpload([]);
	};

	if (indemnityAgreementStatus === "complete" || indemnityAgreementStatus === "review") {
		return (
			<IndemnityAgreementAttachmentCard
				indemnityAgreementId={indemnityAgreementId}
				isComplete={indemnityAgreementStatus === "complete"}
				suretyAccountId={suretyAccountId}
			/>
		);
	}

	return (
		<div className="flex flex-col space-y-[16px]">
			<LocalFileUpload
				value={indemnityAgreementFileUpload}
				allowedTypesAndLabels={{
					[FileType.indemnity_agreement]:
						IndemnityAttachmentTypeLabelMap[FileType.indemnity_agreement],
				}}
				maxFiles={1}
				allowDelete={true}
				allowedExtensions={[FileExtension.pdf]}
				onChange={setIndemnityAgreementFileUpload}
				dropzonePlaceholderText="Signed GIA Upload: Drag and drop or click to select a file"
				analyticsContext="indemnity"
			/>
			<div className="flex flex-row justify-end space-x-[10px]">
				{additionalButtons}
				<ConfirmationModalClickWrapper
					message="Are you sure?"
					subMessage="This will upload the completed indemnity agreement for review."
					shouldConfirm={!!indemnityAgreementFileUpload.length}
					onConfirm={handleUploadCompletedAgreement}
				>
					<Button
						color="primary"
						className="w-fit self-end"
						disabled={!indemnityAgreementFileUpload.length}
					>
						Upload
					</Button>
				</ConfirmationModalClickWrapper>
			</div>
			{uploadCompletedAgreementIsLoading && <LoadingModal />}
		</div>
	);
};
